import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { mockDataManager } from './mockData';

export type MockMode = 'off' | 'capture' | 'replay';

class MockInterceptor {
  private mode: MockMode = 'off';
  private axiosInstance: AxiosInstance | null = null;
  private requestInterceptorId: number | null = null;
  private responseInterceptorId: number | null = null;

  constructor() {
    this.initializeMode();
  }

  private initializeMode(): void {
    const envMode = import.meta.env.VITE_API_MOCK_MODE as MockMode;
    this.mode = envMode || 'off';
    
    if (this.mode !== 'off') {
      console.log(`[Mock Interceptor] Initialized in ${this.mode} mode`);
    }
  }

  public setMode(mode: MockMode): void {
    this.mode = mode;
    console.log(`[Mock Interceptor] Mode set to ${mode}`);
  }

  public getMode(): MockMode {
    return this.mode;
  }

  public setupInterceptors(axiosInstance: AxiosInstance): void {
    this.axiosInstance = axiosInstance;
    
    if (this.mode === 'off') {
      return;
    }

    this.removeInterceptors();

    // Request interceptor for replay mode
    this.requestInterceptorId = axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (this.mode === 'replay') {
          return this.handleReplayRequest(config);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for capture mode
    this.responseInterceptorId = axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (this.mode === 'capture') {
          this.handleCaptureResponse(response);
        }
        return response;
      },
      (error) => {
        // Also capture error responses in capture mode
        if (this.mode === 'capture' && error.response) {
          this.handleCaptureResponse(error.response);
        }
        return Promise.reject(error);
      }
    );
  }

  private handleReplayRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    const mockedResponse = mockDataManager.getResponse(config);
    
    if (mockedResponse) {
      console.log(`[Mock Interceptor] Mock data found for ${config.method?.toUpperCase()} ${config.url}`);
      
      // Store the mock response on the config so we can use it later
      (config as InternalAxiosRequestConfig & { __mockResponse?: unknown }).__mockResponse = mockedResponse;
    } else {
      console.warn(`[Mock Interceptor] No mock data found for ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  }

  private handleCaptureResponse(response: AxiosResponse): void {
    try {
      // Only capture successful responses and meaningful errors
      if (response.status >= 200 && response.status < 300) {
        mockDataManager.saveResponse(response.config, response);
      } else if (response.status >= 400) {
        // Capture error responses too (like 404, 422, etc.)
        mockDataManager.saveResponse(response.config, response);
      }
    } catch (error) {
      console.warn('[Mock Interceptor] Failed to capture response:', error);
    }
  }

  private removeInterceptors(): void {
    if (this.axiosInstance) {
      if (this.requestInterceptorId !== null) {
        this.axiosInstance.interceptors.request.eject(this.requestInterceptorId);
        this.requestInterceptorId = null;
      }
      if (this.responseInterceptorId !== null) {
        this.axiosInstance.interceptors.response.eject(this.responseInterceptorId);
        this.responseInterceptorId = null;
      }
    }
  }

  public destroy(): void {
    this.removeInterceptors();
    this.axiosInstance = null;
  }
}

export const mockInterceptor = new MockInterceptor();

// Development utilities
export const mockInterceptorUtils = {
  setMode: (mode: MockMode) => mockInterceptor.setMode(mode),
  getMode: () => mockInterceptor.getMode(),
  
  // Quick mode switchers
  enableCapture: () => mockInterceptor.setMode('capture'),
  enableReplay: () => mockInterceptor.setMode('replay'),
  disable: () => mockInterceptor.setMode('off'),
  
  // Status check
  isActive: () => mockInterceptor.getMode() !== 'off'
};

// Make utilities available on window for easy access in dev tools
if (import.meta.env.DEV) {
  (window as Window & { mockInterceptorUtils?: typeof mockInterceptorUtils }).mockInterceptorUtils = mockInterceptorUtils;
} 